import React from "react";
import { GEO_STYLES } from "@/lib/geo";
import type { GeometryStatus, Vertex, Wall } from "@/api/types";
import type { EditableGeom } from "./GeometryCanvas2D";

interface Props {
  geom: EditableGeom;
  rotation: number; // degrees
  styleStatus?: GeometryStatus;
  showWalls?: boolean;
  showCeiling?: boolean;
  wireframe?: boolean;
  className?: string;
}

const vById = (vs: Vertex[], id: string) => vs.find((v) => v.id === id);

export const GeometryViewer3D: React.FC<Props> = ({
  geom, rotation, styleStatus, showWalls = true, showCeiling = false, wireframe = false, className = "",
}) => {
  const status = styleStatus || geom.status;
  const style = GEO_STYLES[status];
  const h = geom.height || 2.5;

  // centroid for centering
  const cx = geom.vertices.reduce((s, v) => s + v.x, 0) / (geom.vertices.length || 1);
  const cy = geom.vertices.reduce((s, v) => s + v.y, 0) / (geom.vertices.length || 1);
  const a = (rotation * Math.PI) / 180;
  const tilt = 0.55;

  const project = (x: number, y: number, z: number) => {
    const px = x - cx, py = y - cy;
    const rx = px * Math.cos(a) - py * Math.sin(a);
    const ry = px * Math.sin(a) + py * Math.cos(a);
    return { x: rx, y: ry * tilt - z * 0.95 };
  };

  const floor = geom.vertices.map((v) => project(v.x, v.y, 0));
  const ceil = geom.vertices.map((v) => project(v.x, v.y, h));

  const allPts = [...floor, ...ceil];
  const minX = Math.min(...allPts.map((p) => p.x));
  const maxX = Math.max(...allPts.map((p) => p.x));
  const minY = Math.min(...allPts.map((p) => p.y));
  const maxY = Math.max(...allPts.map((p) => p.y));
  const pad = Math.max(maxX - minX, maxY - minY) * 0.15 + 0.5;
  const vbW = maxX - minX + pad * 2;
  const vbH = maxY - minY + pad * 2;

  const poly = (pts: { x: number; y: number }[]) => pts.map((p) => `${p.x},${p.y}`).join(" ");

  // wall quads from walls, sorted by average depth (ry) so far walls draw first
  const wallQuads = geom.walls.map((wall: Wall) => {
    const ai = geom.vertices.findIndex((v) => v.id === wall.from);
    const bi = geom.vertices.findIndex((v) => v.id === wall.to);
    if (ai < 0 || bi < 0) return null;
    const fa = floor[ai], fb = floor[bi], ca = ceil[ai], cb = ceil[bi];
    const va = vById(geom.vertices, wall.from)!, vb = vById(geom.vertices, wall.to)!;
    const depth = (va.x - cx) * Math.sin(a) + (va.y - cy) * Math.cos(a) + (vb.x - cx) * Math.sin(a) + (vb.y - cy) * Math.cos(a);
    return { id: wall.id, pts: [fa, fb, cb, ca], depth };
  }).filter(Boolean) as { id: string; pts: any[]; depth: number }[];
  wallQuads.sort((p, q) => p.depth - q.depth);

  return (
    <svg
      data-testid="canvas-3d-viewer"
      viewBox={`${minX - pad} ${minY - pad} ${vbW} ${vbH}`}
      className={`w-full rounded-lg bg-[#070B14] tech-grid ${className}`}
      style={{ aspectRatio: `${vbW} / ${vbH}` }}
    >
      {/* floor */}
      <polygon points={poly(floor)} fill={wireframe ? "none" : style.fill} stroke={style.stroke} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeDasharray={style.dash === "none" ? undefined : style.dash} />
      {/* walls */}
      {showWalls && wallQuads.map((w, i) => (
        <polygon
          key={w.id}
          points={poly(w.pts)}
          fill={wireframe ? "none" : style.fill}
          stroke={style.stroke}
          strokeWidth={1.5}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          opacity={wireframe ? 1 : 0.5 + (i / Math.max(wallQuads.length, 1)) * 0.4}
        />
      ))}
      {/* ceiling */}
      {showCeiling && (
        <polygon points={poly(ceil)} fill={wireframe ? "none" : style.fill} stroke={style.stroke} strokeWidth={2} vectorEffect="non-scaling-stroke" opacity={0.9} />
      )}
      {/* vertical edges */}
      {floor.map((f, i) => (
        <line key={i} x1={f.x} y1={f.y} x2={ceil[i].x} y2={ceil[i].y} stroke={style.stroke} strokeWidth={1.5} vectorEffect="non-scaling-stroke" opacity={0.85} />
      ))}
    </svg>
  );
};
